import { api } from '../../fetchWrapper';
import { API_ENDPOINTS, getEndpoint } from '../../apiConstants';
import { 
  GlobalVariable, 
  GlobalVariableApiResponse, 
  GlobalVariableCreatePayload, 
  GlobalVariableUpdatePayload, 
  GlobalVariableSearchParams,
  GlobalVariableList,
  Client,
  JobDetail
} from './globalVariable.types';

// Transform API response to clean UI data
const transformGlobalVariable = (rawData: GlobalVariableApiResponse): GlobalVariable => {
  return {
    globalId: rawData[0],
    clientId: rawData[1],
    jobDetailId: rawData[2],
    globalVariableName: rawData[3],
    globalVariableValue: rawData[4],
    clientName: rawData[5],
    jobName: rawData[6],
  };
};

// Build search parameters
const buildSearchParams = (params: GlobalVariableSearchParams): Record<string, any> => {
  const searchParams: Record<string, any> = {
    searched: true
  };

  if (params.firstResult !== undefined) searchParams.firstResult = params.firstResult;
  if (params.maxResult !== undefined) searchParams.maxResult = params.maxResult;
  if (params.clientId) searchParams.clientId = params.clientId;
  if (params.jobDetailId) searchParams.jobDetailId = params.jobDetailId;
  if (params.globalVariableName) searchParams.globalVariableName = params.globalVariableName;

  return searchParams;
};

// Global Variable API
export const globalVariableApi = {
  // Get list of global variables
  fetch: async ({ 
    clientCode, 
    ...params 
  }: { 
    clientCode: string 
  } & GlobalVariableSearchParams): Promise<GlobalVariable[]> => {
    const endpoint = getEndpoint(clientCode, API_ENDPOINTS.GLOBAL_VARIABLE.LIST);
    const response: GlobalVariableApiResponse[] = await api.get(endpoint, buildSearchParams(params));
    return response.map(transformGlobalVariable);
  },

  // Get count of global variables
  count: async ({ 
    clientCode, 
    ...params 
  }: { 
    clientCode: string 
  } & Omit<GlobalVariableSearchParams, 'firstResult' | 'maxResult'>): Promise<number> => {
    const endpoint = getEndpoint(clientCode, API_ENDPOINTS.GLOBAL_VARIABLE.COUNT);
    const response = await api.get(endpoint, buildSearchParams(params));
    
    if (typeof response === 'number') {
      return response;
    } else if (response && typeof response === 'object' && 'count' in response) {
      return response.count as number;
    } else {
      return 0;
    }
  },

  // Get global variables with count
  fetchWithCount: async ({ 
    clientCode, 
    ...params 
  }: { 
    clientCode: string 
  } & GlobalVariableSearchParams): Promise<GlobalVariableList> => {
    const [data, total] = await Promise.all([
      globalVariableApi.fetch({ clientCode, ...params }),
      globalVariableApi.count({ clientCode, ...params })
    ]);

    return { data, total };
  },

  // Create new global variable
  create: async ({ 
    clientCode, 
    payload 
  }: { 
    clientCode: string, 
    payload: GlobalVariableCreatePayload 
  }): Promise<any> => {
    const endpoint = getEndpoint(clientCode, API_ENDPOINTS.GLOBAL_VARIABLE.CREATE);
    const response = await api.post(endpoint, payload);
    return response;
  },

  // Update existing global variable
  update: async ({ 
    clientCode, 
    payload 
  }: { 
    clientCode: string, 
    payload: GlobalVariableUpdatePayload 
  }): Promise<any> => {
    const endpoint = getEndpoint(clientCode, API_ENDPOINTS.GLOBAL_VARIABLE.UPDATE);
    const response = await api.put(endpoint, payload);
    return response;
  },

  // Delete global variable by id
  delete: async ({ 
    clientCode, 
    id 
  }: { 
    clientCode: string; 
    id: number;
  }): Promise<void> => {
    const endpoint = getEndpoint(clientCode, `${API_ENDPOINTS.GLOBAL_VARIABLE.DELETE}${id}`);
    await api.delete(endpoint);
  },

  // Get all clients for dropdown
  fetchClients: async ({ clientCode }: { clientCode: string }): Promise<Client[]> => {
    const endpoint = getEndpoint(clientCode, API_ENDPOINTS.CLIENT.LIST);
    const response: any[] = await api.get(endpoint, { firstResult: -1, maxResult: 0 });
    return response.map((client: any, index: number) => ({
      id: client[0] || index,
      name: client[1] || `Client ${index + 1}`,
    }));
  },

  // Get all job details for dropdown
  fetchJobDetails: async ({ clientCode }: { clientCode: string }): Promise<JobDetail[]> => {
    const endpoint = getEndpoint(clientCode, API_ENDPOINTS.JOB.DETAIL_LIST);
    const response: any[] = await api.get(endpoint, { firstResult: -1, maxResult: 0 });
    return response.map((job: any, index: number) => ({
      id: job[0] || index,
      name: job[1] || `Job ${index + 1}`,
      clientId: job[2] || 0,
    }));
  },

  // Get job details filtered by client for dropdown
  fetchJobDetailsByClient: async ({ 
    clientCode, 
    clientId 
  }: { 
    clientCode: string;
    clientId?: number;
  }): Promise<JobDetail[]> => {
    const endpoint = getEndpoint(clientCode, API_ENDPOINTS.JOB.DETAIL_LIST);
    const params: any = { firstResult: -1, maxResult: 0 };
    if (clientId) {
      params.clientId = clientId;
      params.searched = true;
    }
    const response: any[] = await api.get(endpoint, params);
    return response.map((job: any, index: number) => ({
      id: job[0] || index,
      name: job[1] || `Job ${index + 1}`,
      clientId: job[2] || 0,
    }));
  },

  // Get global variable names for dropdown
  fetchGlobalVariableNames: async ({ 
    clientCode, 
    clientId, 
    jobDetailId 
  }: { 
    clientCode: string;
    clientId?: number;
    jobDetailId?: number;
  }): Promise<string[]> => {
    const endpoint = getEndpoint(clientCode, API_ENDPOINTS.GLOBAL_VARIABLE.LIST);
    const params: any = { firstResult: -1, maxResult: 0 };
    if (clientId) params.clientId = clientId;
    if (jobDetailId) params.jobDetailId = jobDetailId;
    if (clientId || jobDetailId) params.searched = true;
    
    const response: any[] = await api.get(endpoint, params);
    // Extract unique variable names from the response
    const variableNames = new Set<string>();
    response.forEach((variable: any) => {
      if (variable[3]) { // globalVariableName is at index 3
        variableNames.add(variable[3]);
      }
    });
    return Array.from(variableNames);
  },
};

// Legacy exports for backward compatibility
export const fetchGlobalVariables = globalVariableApi.fetch;
export const fetchGlobalVariablesCount = globalVariableApi.count;
export const createGlobalVariable = globalVariableApi.create;
export const updateGlobalVariable = globalVariableApi.update;
